import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ErrorIcon from '@mui/icons-material/Error'

import { theme } from '../../themes/theme'
import axios from '../../dataClient/dataClient'
import { handleDownloadResponse } from '../../util/downloadFile'
import {
  StyledDialog,
  FlexDialogContent,
  FlexColumn,
  SpaceBetweenRowContainer,
  FlexRow,
  CenteredContent,
  CenteredFlexColumn
} from '../styles/Containers'
import { InfoMessage, WarningMessage } from '../common/UserMessage'
import BodyText, { BoldFormTitle, BoldSmallBodyText, SmallBodyText, Subheading } from '../styles/Text'
import { SecondaryButton, SecondaryIconButton } from '../common/Button'
import DuplicateGroup from './DuplicateGroup'
import Loading from '../common/Loading'

const WarningButton = styled(SecondaryButton)`
  background: rgba(208, 198, 16, 0.1);
  gap: 10px;

  :hover:not([disabled]) {
    background: rgba(208, 198, 16, 0.3);
  }

  .MuiSvgIcon-root {
    color: rgba(208, 198, 16, 1);
    width: 15px;
    height: 15px;
    transform: scale(1.4);
  }
`

const Dialog = styled(StyledDialog)`
  .MuiDialog-paperWidthSm {
    min-height: calc(100% - 60px);
  }
`

const ContentContainer = styled(FlexRow)`
  flex: 1;
  min-height: 0;
  gap: 1%;
`

const GroupContainer = styled(FlexColumn)`
  min-height: fit-content;
  width: 30%;
  min-width: 30%;
`

const PDFContainer = styled(FlexColumn)`
  width: 29%;
  min-width: 29%;
  flex-grow: 1;
  min-height: 0;
`

const Pdf = styled.iframe`
  border: none;
  display: block;
  min-height: 0;
  flex-grow: 1;
`

const LoadingErrorContainer = styled(CenteredContent)`
  border-radius: ${theme.borderRadius.div};
  border: 1px solid ${theme.colors.sectionDivider};
`

const FilenameBar = styled.div`
  flex-shrink: 0;
  min-width: 0;
  padding: 12px 16px;
  background: ${theme.colors.background.light};
  border-bottom: none;
  border-radius: ${theme.borderRadius.div} ${theme.borderRadius.div} 0 0;
`

const FilenameTitle = styled(BoldFormTitle.withComponent('h3'))`
  font-size: 16px;
  line-height: 1.35;
  text-transform: none;
  color: ${theme.colors.text.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

function DeduplicationButton({ categoryId, duplicateGroups = [], onClose = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)

  const [groupsChanged, setGroupsChanged] = useState(false)
  const [formattedGroups, setFormattedGroups] = useState()
  const [activeGroupIndex, setActiveGroupIndex] = useState()
  const [activePdf1, setActivePdf1] = useState()
  const [loadingPdf1, setLoadingPdf1] = useState(false)
  const [errorPdf1, setErrorPdf1] = useState(false)
  const [activePdf2, setActivePdf2] = useState()
  const [loadingPdf2, setLoadingPdf2] = useState(false)
  const [errorPdf2, setErrorPdf2] = useState(false)

  const prevPdf1Ref = useRef()
  const prevPdf2Ref = useRef()

  const handleOpen = () => {
    if (activePdf1 !== undefined) updateActiveFile(activePdf1.id, true)
    if (activePdf2 !== undefined) updateActiveFile(activePdf2.id, false)

    setIsVisible(true)
  }
  const handleClose = () => {
    setIsVisible(false)
    onClose()
  }

  const handleNext = () => setActiveGroupIndex(activeGroupIndex => activeGroupIndex + 1)
  const handleBack = () => setActiveGroupIndex(activeGroupIndex => activeGroupIndex - 1)

  async function handleFileDownload(file, updateFile1) {
    updateFile1 ? setActivePdf1() : setActivePdf2()
    updateFile1 ? setErrorPdf1(false) : setErrorPdf2(false)
    updateFile1 ? setLoadingPdf1(true) : setLoadingPdf2(true)
    try {
      const response = await axios.get(`/file/${file.id}/download`, { responseType: 'blob' })
      return handleDownloadResponse(response, file.filename, 'view')
    } catch (e) {
      console.error('An error occurred while fetching the document', file.filename, e)
      updateFile1 ? setErrorPdf1(true) : setErrorPdf2(true)
    } finally {
      updateFile1 ? setLoadingPdf1(false) : setLoadingPdf2(false)
    }
  }

  async function updateActiveFile(fileId, updateFile1) {
    const file = formattedGroups[activeGroupIndex].files.find(f => f.id === fileId)
    const pdfUrl = await handleFileDownload(file, updateFile1)

    const newActiveFile = {
      id: fileId,
      filename: file.filename,
      pdfUrl
    }

    if (updateFile1) setActivePdf1(newActiveFile)
    else setActivePdf2(newActiveFile)
  }

  async function onUpdateActiveFiles(fileId, updateFile1) {
    const matchesId1 = activePdf1 === undefined ? false : activePdf1.id === fileId
    const matchesId2 = activePdf2 === undefined ? false : activePdf2.id === fileId

    if (!matchesId1 && !matchesId2) {
      updateActiveFile(fileId, updateFile1)
    }
  }

  async function handleSavedSelection(groupFiles) {
    const removedIds = groupFiles.filter(f => !f.shouldKeep).map(f => f.fileId)
    const files = formattedGroups[activeGroupIndex].files.map(f => {
      return { ...f, removed: removedIds.includes(f.id) }
    })

    setFormattedGroups(prev => {
      prev[activeGroupIndex] = { isReviewed: true, files }
      return [...prev]
    })

    if (activeGroupIndex !== formattedGroups.length - 1) handleNext()
  }

  useEffect(() => {
    if (activeGroupIndex !== undefined && formattedGroups[activeGroupIndex].files.length > 1) {
      onUpdateActiveFiles(formattedGroups[activeGroupIndex].files[0].id, true)
      onUpdateActiveFiles(formattedGroups[activeGroupIndex].files[1].id, false)
    }
  }, [activeGroupIndex])

  useEffect(() => {
    if (formattedGroups && formattedGroups.length > 0 && groupsChanged) {
      setGroupsChanged(false)
      if (activeGroupIndex === 0 && formattedGroups[activeGroupIndex].files.length > 1) {
        onUpdateActiveFiles(formattedGroups[0].files[0].id, true)
        onUpdateActiveFiles(formattedGroups[0].files[1].id, false)
      } else {
        setActiveGroupIndex(0)
      }
    }
  }, [formattedGroups])

  useEffect(() => {
    setActivePdf1()
    setActivePdf2()
    setActiveGroupIndex()
    setGroupsChanged(true)

    if (duplicateGroups) {
      const groupsToInclude = duplicateGroups.filter(g => g.length > 1)

      if (groupsToInclude.length > 0) {
        const groups = groupsToInclude.map(g => {
          return { isReviewed: false, files: g }
        })
        setFormattedGroups(groups)
      } else {
        setFormattedGroups(prev => (prev?.length === 0 ? prev : []))
      }
    }
  }, [duplicateGroups])

  useEffect(() => {
    if (prevPdf1Ref.current?.pdfUrl && prevPdf1Ref.current.pdfUrl !== activePdf1?.pdfUrl) URL.revokeObjectURL(prevPdf1Ref.current.pdfUrl)
    if (prevPdf2Ref.current?.pdfUrl && prevPdf2Ref.current.pdfUrl !== activePdf2?.pdfUrl) URL.revokeObjectURL(prevPdf2Ref.current.pdfUrl)

    prevPdf1Ref.current = activePdf1
    prevPdf2Ref.current = activePdf2

    return () => {
      if (activePdf1?.pdfUrl) URL.revokeObjectURL(activePdf1.pdfUrl)
      if (activePdf2?.pdfUrl) URL.revokeObjectURL(activePdf2.pdfUrl)
    }
  }, [activePdf1, activePdf2])

  return (
    <>
      {formattedGroups &&
        (formattedGroups.length === 0 ? (
          <SmallBodyText>No duplicates found.</SmallBodyText>
        ) : (
          <>
            <WarningButton onClick={handleOpen}>
              <ErrorIcon />
              Review Duplicates
            </WarningButton>
            <Dialog maxwidth="100%" open={isVisible} onClose={handleClose} keepMounted={false}>
              <FlexDialogContent>
                <SpaceBetweenRowContainer>
                  <BoldFormTitle>Deduplication</BoldFormTitle>
                  <SecondaryIconButton onClick={handleClose} aria-label="Close Deduplication Pop Up">
                    <CloseIcon />
                  </SecondaryIconButton>
                </SpaceBetweenRowContainer>
                {activeGroupIndex !== undefined && (
                  <ContentContainer>
                    <GroupContainer>
                      <WarningMessage>
                        <FlexColumn gap="5px">
                          Potential duplicate groups found: {formattedGroups.length}
                          {formattedGroups.filter(g => !g.isReviewed).length === 0 && (
                            <BoldSmallBodyText>All groups have been reviewed.</BoldSmallBodyText>
                          )}
                        </FlexColumn>
                      </WarningMessage>
                      <SpaceBetweenRowContainer>
                        <SecondaryIconButton onClick={handleBack} disabled={activeGroupIndex === 0} aria-label="View Previous Group">
                          <ArrowBackIcon />
                        </SecondaryIconButton>
                        <Subheading>Group {activeGroupIndex + 1}</Subheading>
                        <SecondaryIconButton
                          onClick={handleNext}
                          disabled={activeGroupIndex === formattedGroups.length - 1}
                          aria-label="View Next Group">
                          <ArrowForwardIcon />
                        </SecondaryIconButton>
                      </SpaceBetweenRowContainer>
                      {formattedGroups[activeGroupIndex].isReviewed && <InfoMessage>This group has been reviewed.</InfoMessage>}
                      <DuplicateGroup
                        categoryId={categoryId}
                        isReviewed={formattedGroups[activeGroupIndex].isReviewed}
                        files={formattedGroups[activeGroupIndex].files}
                        activeFiles={activePdf1 && activePdf2 ? [activePdf1.id, activePdf2.id] : []}
                        hasNextGroup={activeGroupIndex !== formattedGroups.length - 1}
                        onUpdateActiveFiles={onUpdateActiveFiles}
                        onSuccess={handleSavedSelection}
                      />
                    </GroupContainer>
                    <PDFContainer gap="0px">
                      {activePdf1 && (
                        <FilenameBar title={activePdf1.filename}>
                          <FilenameTitle>{activePdf1.filename}</FilenameTitle>
                        </FilenameBar>
                      )}
                      {loadingPdf1 && (
                        <LoadingErrorContainer>
                          <Loading />
                        </LoadingErrorContainer>
                      )}
                      {activePdf1 && errorPdf1 && (
                        <LoadingErrorContainer>
                          <CenteredFlexColumn>
                            <BodyText>We're sorry.</BodyText>
                            <BodyText>There was an error loading your file. Please try again.</BodyText>
                            <SecondaryButton onClick={() => updateActiveFile(activePdf1.id, true)}>Reload File</SecondaryButton>
                          </CenteredFlexColumn>
                        </LoadingErrorContainer>
                      )}
                      {activePdf1 && !loadingPdf1 && !errorPdf1 && (
                        <Pdf src={`${activePdf1.pdfUrl}#view=fitH`} title="File preview" height="100%" width="100%" type="application/pdf" />
                      )}
                    </PDFContainer>
                    <PDFContainer gap="0">
                      {activePdf2 && (
                        <FilenameBar title={activePdf2.filename}>
                          <FilenameTitle>{activePdf2.filename}</FilenameTitle>
                        </FilenameBar>
                      )}
                      {loadingPdf2 && (
                        <LoadingErrorContainer>
                          <Loading />
                        </LoadingErrorContainer>
                      )}
                      {activePdf2 && errorPdf2 && (
                        <LoadingErrorContainer>
                          <CenteredFlexColumn>
                            <BodyText>We're sorry.</BodyText>
                            <BodyText>There was an error loading your file. Please try again.</BodyText>
                            <SecondaryButton onClick={() => updateActiveFile(activePdf2.id, false)}>Reload File</SecondaryButton>
                          </CenteredFlexColumn>
                        </LoadingErrorContainer>
                      )}
                      {activePdf2 && !loadingPdf2 && !errorPdf2 && (
                        <Pdf src={`${activePdf2.pdfUrl}#view=fitH`} title="File preview" height="100%" width="100%" type="application/pdf" />
                      )}
                    </PDFContainer>
                  </ContentContainer>
                )}
              </FlexDialogContent>
            </Dialog>
          </>
        ))}
    </>
  )
}

DeduplicationButton.propTypes = {
  categoryId: PropTypes.string.isRequired,
  duplicateGroups: PropTypes.array,
  onClose: PropTypes.func
}

export default DeduplicationButton
